/* Identity + sign out, as two rows of the Account group.

   Sign out is hidden when auth isn't configured (local dev / pytest run open as
   the owner, so the button would do nothing). /api/auth/config reports this.

   It is a row rather than a bordered button (ADR 0011), which also gives it a
   thumb-sized target the old `g-button full` did not have. It carries `sep`, one
   of the two deliberate hairlines left in this workspace: ending a session is an
   action, and should not read as the third line of the identity block above it. */

import { LitElement, html, css } from 'lit';
import { shadowReset } from '../../design/base.js';
import { settingsRow } from './row.js';
import { customElement, property } from 'lit/decorators.js';
import type { Me } from '../../core/types.js';
import '../../component/g-avatar/g-avatar.js';
import '../../component/g-notice/g-notice.js';

@customElement('settings-account')
export class SettingsAccount extends LitElement {
  static override styles = [
    shadowReset,
    settingsRow,
    css`
      :host {
        display: block;
      }
      /* align-items: center from .row is right for a single-line control but
         wrong here — the avatar should sit beside the name, and the block below
         it can wrap to two lines on a narrow phone. */
      .who {
        align-items: flex-start;
      }
      g-avatar {
        --avatar-size: 40px;
      }
      .id {
        min-width: 0;
      }
      .name {
        font-weight: 700;
      }
      .email {
        color: var(--text-dim);
        font-family: var(--font-mono);
        font-size: 0.75rem;
        word-break: break-all;
      }
      .admin {
        margin-left: var(--space-2);
        padding: 1px var(--space-2);
        border-radius: var(--radius-full);
        background: color-mix(in oklab, var(--accent) 18%, transparent);
        color: var(--accent);
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }
      /* Quiet at rest (§31) and only under the finger does it read as the
         session-ending action it is — the same treatment g-button gives
         variant="danger", reproduced here because this is a local button. */
      .out:active,
      .out:focus-visible {
        color: var(--danger);
      }
      @media (hover: hover) {
        .out:hover {
          color: var(--danger);
        }
      }
    `,
  ];

  @property({ attribute: false }) me: Me | null = null;
  @property({ type: Boolean }) authEnabled = false;

  override render() {
    if (!this.me) return html`<g-notice class="text">Not signed in.</g-notice>`;
    return html`
      <div class="row who">
        <g-avatar .me=${this.me}></g-avatar>
        <div class="id">
          <div class="name">
            ${this.me.display_name || this.me.email}
            ${this.me.is_admin ? html`<span class="admin">admin</span>` : ''}
          </div>
          <div class="email">${this.me.email}</div>
        </div>
      </div>
      ${this.authEnabled
        ? html`<button
            class="row sep out"
            @click=${() =>
              this.dispatchEvent(new CustomEvent('sign-out', { bubbles: true, composed: true }))}
          >
            Sign out
          </button>`
        : ''}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'settings-account': SettingsAccount;
  }
}
